import styled from "styled-components";
import { colors } from "../../utils";
import { device } from "../../utils/device";
import { mq } from "../../utils/breakpoints";
import { motion } from "framer-motion";
import {
  BUSINESS_BOX_LEFT_INSET_PX,
  BUSINESS_BOX_WIDTH_PX,
} from "../WheelGallery/BusinessBox/styles";
import { HEADER_SEARCH_BAR_LEFT_INSET_PX } from "../WheelGallery/Header/styles";

// The business box's own right edge, measured from the shared viewport-left
// origin both the header and <main> render from (pages/Gallery/index.jsx has
// no ancestor max-width/centering wrapper on either side — confirmed live).
const BUSINESS_BOX_RIGHT_EDGE_PX =
  BUSINESS_BOX_WIDTH_PX + BUSINESS_BOX_LEFT_INSET_PX;

// ISP-9999 / QA-149 #2 (Kelly/Gordon, 2026-08-20): "search bar window right
// edge aligns with the side of the business box." The PREVIOUS QA-149 fix
// (below) capped this field's width at BUSINESS_BOX_WIDTH_PX, which only
// matches WIDTHS — it silently assumed the two boxes share a left inset, but
// MEASURED live (mocked local render, 1280/1440/1920px) the SearchBar's own
// Container starts HEADER_SEARCH_BAR_LEFT_INSET_PX from the header's left
// edge (FlexContainer's margin + the logo's footprint), while the business
// box starts only BUSINESS_BOX_LEFT_INSET_PX from <main>'s left edge — a
// different, larger inset that pushed the search field's right edge ~64-69px
// past the card's. Subtracting that inset delta from the ceiling instead
// lands both right edges on the SAME pixel (360px in the measured layout).
export const SEARCH_BAR_MAX_WIDTH_PX =
  BUSINESS_BOX_RIGHT_EDGE_PX - HEADER_SEARCH_BAR_LEFT_INSET_PX;

export const Container = styled.div`
  min-width: 180px;
  width: 30vw;
  max-width: ${SEARCH_BAR_MAX_WIDTH_PX}px;

  @media (${device.mobilePortrait}) {
    min-width: 10px;
    width: 100%;
  }

  @media (${device.mobileLandscape}) {
    flex-grow: 1;
  }

  /* ISP-4018: the 180px floor above is orientation-scoped (the mobilePortrait
     override only lifts it in PORTRAIT), so a narrow LANDSCAPE viewport kept a
     180px search field and forced the header wider than the screen. Below the
     tablet-portrait cut the field must always be allowed to shrink; it never
     shrinks when there is room, because width/max-width still govern. */
  ${mq.tabletP} {
    min-width: 0;
  }
`;

export const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${colors.white};
  border: 1px solid #ced4da;
  padding: 5px 10px;
  border-radius: 8px;
  height: 5vh;
  align-items: center;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  /* daniyal-0918-staging-header-search-click-shows-border: dev already carries
     this fix (src/styles/focusRing.css's app-wide :focus-visible:focus-visible
     rule was landing a plain, oddly-inset purple outline directly on the
     <input> instead of the ONE ring the QA-80 design law calls for — "the
     ONLY indicator should be a single purple outline around the ENTIRE
     component's border"). Moving the visible keyboard-focus indicator onto
     THIS bordered field container, and opting the inner <input> out below,
     ports that single-ring behavior here. */
  &:focus-within {
    border-color: ${colors.PrimaryPurple500};
    box-shadow: 0 0 0 3px rgba(155, 90, 248, 0.25);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (${device.mobileLandscape}) {
    height: 40px;
    border-radius: 4px;
  }
`;
export const SearchIcon = styled(motion.img)`
  position: absolute; /* Absolute positioning */
  left: 10px; /* Adjust based on your padding */
  top: 25%;
  transform: translateY(-50%);
  width: 20px; /* Set desired size */
  height: 20px;
  object-fit: contain; /* Ensures the icon scales correctly */
  object-position: center;
  display: block; /* Removes any extra space below the image */
`;

export const ClearIcon = styled(motion.img)`
  cursor: pointer;
  position: absolute;
  right: 10px; /* Space from the right edge */
  top: 25%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  object-fit: contain;
  object-position: center;
  display: block;
`;

export const TextInput = styled.input`
  outline: none;
  border: none;
  width: 100%;
  /* D1 (a11y-i18n-mobileweb matrix): the parent SearchContainer carries a
     min-height:40px floor, but this <input> itself had no height rule and
     sat at its intrinsic content height (~26px) inside that taller box — an
     independent qa-validator critic measured the raw <input> at 80.11x26px
     even after the container fix, because SearchContainer's extra vertical
     space was just padding AROUND a short input, not a taller input; a tap
     in that padding does not focus the field (no click-through handler).
     height:100% makes the actual focusable/clickable element fill its
     parent's real box, so the visible ~40px field and the real hit target
     are the SAME size. */
  height: 100%;
  padding-left: 30px;

  &::placeholder {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    font-family: "DM Sans", sans-serif;
    color: #212529;
  }

  /* QA-80 (Gordon, 2026-08-04 -- universal design law): "the ONLY indicator
     should be a single purple outline around the ENTIRE component's border."
     SearchContainer above already paints that ONE ring on :focus-within
     (border-color + a 3px purple halo). Without this opt-out,
     src/styles/focusRing.css's app-wide :focus-visible:focus-visible rule
     (specificity (0,2,0)) beats the plain "outline: none" above it (0,1,0)
     regardless of source order, and paints a SECOND purple outline directly
     on this <input> -- the same double-ring defect fixed on the Capture
     search bar (src/pages/Capture/styles.js). Doubled pseudo-class to match
     that (0,2,0) specificity; styled-components injects after the static
     global sheet, so equal specificity then resolves by source order in
     this rule's favor. */
  &:focus-visible:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

export const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  width: 30vw;
  background-color: ${colors.white};
  box-shadow: 0px 0px 9.3px 0px #3b3b3b1c;
  border-radius: 8px;
  // overflow-y: auto;
  // overflow-x: hidden;
  overflow: hidden;
  z-index: 10000;
  padding-left: 20px;
  padding-right: 10px;
  max-height: 405px;
  padding-bottom: 5px;

  @media (${device.mobilePortrait}) {
    width: 100vw;
    height: 100vh;
    left: 0;
    border-radius: 0;
  }

  @media (${device.mobileLandscape}) {
    width: 100vw;
    left: 0;
    border-radius: 0;
    top: 60px;
    padding-left: 60px;
  }
`;

export const TabsContainer = styled.div`
  position: sticky;
  top: 0;
  background-color: ${colors.white};
  z-index: 1001; /* Ensure it stays above the scrollable content */
`;

export const ItemsContainer = styled.div`
  flex: 1; /* Take up remaining space */
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 10px; /* Optional: To match padding in ResultsContainer */
  padding-left: 5px;

  scroll-behavior: smooth; /* Smooth scrolling experience */
`;

export const InfiniteLoader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

export const ResultType = styled.div`
  margin-top: 5px;
  margin-bottom: 5px;
  min-width: 0;
`;

/** Fixed-size clip so WheelComponent’s SVG/portrait layers cannot overlap the title */
export const SEARCH_RESULT_WHEEL_SIZE_PX = 60;

export const SearchItemMedia = styled.div`
  flex-shrink: 0;
  width: ${SEARCH_RESULT_WHEEL_SIZE_PX}px;
  height: ${SEARCH_RESULT_WHEEL_SIZE_PX}px;
  overflow: hidden;
  position: relative;
`;

export const BottomView = styled.div`
  padding: 10px;
  border-top: 1px solid ${colors.lightGrey};
  margin-top: 10px;
  &:hover {
    background-color: #f0f0f0;
    cursor: pointer;
  }

  @media (${device.mobileLandscape}) {
    display: none;
  }

  @media (${device.mobilePortrait}) {
    display: none;
  }
`;

export const SearchItem = styled(motion.div)`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 10px 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.3s ease;
`;

/** Lets text truncate/wrap inside narrow dropdowns (flex default min-width:auto clips overflow) */
export const SearchItemDetails = styled.div`
  margin-left: 10px;
  flex: 1 1 0%;
  min-width: 0;
`;

export const Loader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
`;

// Screen-reader-only announcement target for the results dropdown. Scoped to the
// SearchBar on purpose: the page's existing role="status" regions belong to the
// SPA RouteAnnouncer, and hijacking those to say "10 users found." would corrupt
// route announcements for every other screen.
// Standard clip-rect technique (same as Help.styled / FirstRunIntro.styled):
// removed from the visual layer WITHOUT display:none / visibility:hidden, both of
// which take a node out of the accessibility tree and would silence it entirely.
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
